// The event at a glance (Lane 1344, UI redesign slice 1; Ryan round 4): current
// status, parts by category, suppliers, the schedule and the latest activity.
// Every figure is the server's; the browser only formats it.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { STALE_MESSAGE, formatDate, formatMoney } from './sourcingApi';
import { GOAL_MAX, GOAL_MAX_OWN_WORDS, GOAL_PICKS, GOAL_RAW_MAX, cleanGoal, goalLength } from './goal';
import './goal.css';
import { formatDueAt } from '../sourcing-portal/portalView';
import { ACTION_TEXT } from './HistoryTab';
import ScheduleSettings, { PLAN_STATE_TEXT, planRows } from './ScheduleSettings';
import StatTile from './StatTile';
import useAutoRecalculate from './useAutoRecalculate';
import { shownTerms, termRows } from './eventTerms';
import InvitationTracker, { changeText } from './InvitationTracker';
import RfpSetupCard from './RfpSetupCard';
import './invitations.css';

const PLAN_PILL = { planned: 'srce-pill--info', passed: 'srce-pill--warn', conflict: 'srce-pill--warn', none: 'srce-pill--gray' };

const RESPONSE_PILL = {
  submitted: 'srce-pill srce-pill--ok', declined: 'srce-pill srce-pill--gray', selected: 'srce-pill srce-pill--gray',
  removed: 'srce-pill srce-pill--gray',
};

function shortDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// The change against today as a tile figure: text and a tone. The tile colours it and the
// figure keeps the tile's size (Lane 1362).
function change(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return { value: changeText(n), tone: n >= 0 ? 'good' : 'crit' };
}

// The last answer while a new one is calculated (Lane 1393, Ryan: the figures vanished
// until a click): the earlier figure stays, and the note says it is updating.
// The bids tile's note (Lane 1395): from round 2, how many sent new prices this round;
// declines either way.
function bidsNote(data) {
  const b = data.bids;
  const declined = b.declined ? `${b.declined} declined` : 'No one declined';
  if ((data.current_round || 1) < 2) return declined;
  return `${b.received} updated in round ${data.current_round}${b.declined ? `, ${declined}` : ''}`;
}

const hasFigure = (rec) => rec && rec.projected !== null && rec.projected !== undefined;
const isEarlier = (rec) => Boolean(rec && ['out_of_date', 'calculating'].includes(rec.state) && hasFigure(rec));

function recommendationTile(rec, isDraft) {
  if (!rec) return isDraft ? { value: '-', note: 'After publish' } : { value: 'Not calculated', note: 'Calculate on Scenarios' };
  if (isEarlier(rec)) {
    // "Updating" only while it calculates; out of date with no calculation running (a
    // request that failed) points to Scenarios (Codex Lane 1393 r1 #1).
    const updating = rec.state === 'calculating';
    // The earlier answer keeps its proof words (Codex Lane 1393 r2).
    const proof = rec.result_status === 'feasible' ? ', not proven best' : '';
    return { value: formatMoney(Number(rec.projected)),
      note: `${rec.preferred ? 'Preferred' : 'Earlier figure'}${proof}; ${updating ? 'updating with the latest changes' : 'recalculate on Scenarios'}` };
  }
  switch (rec.state) {
    case 'done': return { value: formatMoney(Number(rec.projected)), note: rec.result_status === 'feasible' ? 'Best found, not proven best' : 'Projected cost', good: true };
    case 'calculating': return { value: 'Calculating', note: 'A few seconds' };
    case 'out_of_date': return { value: rec.preferred ? 'Preferred, out of date' : 'Out of date',
      note: 'Calculating again', warn: true };
    case 'cannot_be_done': return { value: 'Cannot be done', note: 'See Scenarios', warn: true };
    default: return { value: 'Not calculated', note: 'Calculate on Scenarios' };
  }
}

// The event goal (Lane 1354, negotiation PLAN section 4): internal, never shown to a
// supplier. With the chat intake on (Lane 1368, plan B3.8; the server sends
// goal_own_words) it is the buyer's own words: several lines, no picks, and the event
// chat can prepare it too. Off, this card is exactly as before.
// Lane 1368 (plan A4), rewritten by Lane 1375 (Ryan R3): on a draft, the "Approach":
// in plain words what Wingman will do, then the rules it applies, each at full size.
// The goal itself shows once, on the Goal card; there is no "not checked" list. Every
// word is the server's (recommend_plan), present only with the chat intake switch on.
function ApproachCard({ plan }) {
  return (
    <section className="srce-card2" data-testid="srce-recommend-plan">
      <div className="srce-card2-h"><span className="srce-card2-t">Approach</span></div>
      <div className="srce-card2-f srce-col srce-approach">
        {plan.steps.map((text, i) => <p key={i} className="srce-approach-p">{text}</p>)}
        {plan.rules.length > 0 && (
          <>
            <span className="srce-strong">Rules Wingman applies</span>
            <ul className="srce-approach-rules">
              {plan.rules.map((r, i) => (
                <li key={i}>
                  {r.text}
                  {r.from_goal && <span className="srce-pill srce-pill--info srce-approach-tag">From your goal</span>}
                  {r.covers_goal && <span className="srce-pill srce-pill--info srce-approach-tag">Already covers your goal</span>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </section>
  );
}

function GoalCard({ api, eventId, data, onSaved, onStale, notice }) {
  const ownWords = data.goal_own_words === true;
  const [editing, setEditing] = useState(null);   // the revision the edit started from
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  // A save that finishes after this card left (another event, another tab) does
  // nothing (Codex S2a r2 #2).
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // After a conflict the event is reloaded; the draft stays and the next save uses
  // the newer revision, once the buyer has seen the goal now saved (Codex S2a r2 #3).
  useEffect(() => {
    if (!conflict || editing === null || data.revision === editing) return;
    setConflict(false);
    setEditing(data.revision);
    setError(data.goal ? `The event changed while you were editing. The goal saved now is: "${data.goal}". Save again to replace it.`
      : 'The event changed while you were editing. Save again to set your goal.');
  }, [conflict, editing, data.revision, data.goal]);
  const canEdit = ['draft', 'open', 'closed'].includes(data.status);
  const ownLength = ownWords ? goalLength(cleanGoal(text, true)) : 0;
  const tooLong = ownWords && ownLength > GOAL_MAX_OWN_WORDS;
  const start = () => { setText(data.goal || ''); setError(''); setConflict(false); setEditing(data.revision); };
  const save = async () => {
    if (tooLong) return;
    setBusy(true);
    setError('');
    try {
      await api.act(eventId, 'POST', '/goal', { goal: cleanGoal(text, ownWords) || null }, editing);
      if (!alive.current) return;
      setEditing(null);
      onSaved();
    } catch (e) {
      if (!alive.current) return;
      if (e.status === 409 && e.code === 'stale_revision') {
        setError(STALE_MESSAGE);
        setConflict(true);
        onStale();
      } else {
        setError(e.message);
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <section className="srce-card2" data-testid="srce-goal">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Goal</span>
        <span className="srce-pill srce-pill--gray">Your team only</span>
        {canEdit && editing === null && (
          <button type="button" className="ll-clr srce-push" onClick={start}>{data.goal ? 'Edit' : 'Set goal'}</button>)}
      </div>
      {editing === null ? (
        <div className="srce-card2-f">
          {data.goal ? <span className={`srce-strong${data.goal.includes('\n') ? ' srce-goal-lines' : ''}`}>{data.goal}</span>
            : <span className="srce-mut">No goal yet. The goal guides the negotiation prep; suppliers never see it.</span>}
        </div>
      ) : (
        <div className="srce-card2-f srce-goal-edit">
          {ownWords ? (
            <>
              <textarea className="srce-input srce-goal-input srce-goal-text" value={text} maxLength={GOAL_RAW_MAX}
                rows={5} autoFocus disabled={busy} aria-label="Goal"
                placeholder="What do you want to achieve with this event? Write it in your own words."
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                  // Enter starts a new line; Ctrl or Cmd with Enter saves.
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !busy) save();
                  if (e.key === 'Escape') setEditing(null);
                }} />
              <span className={tooLong ? 'srce-error' : 'srce-mut'} role={tooLong ? 'alert' : undefined}>
                {ownLength.toLocaleString()} of {GOAL_MAX_OWN_WORDS.toLocaleString()} characters
                {tooLong ? '. Shorten it to save.' : ''}
              </span>
            </>
          ) : (<>
          <input className="srce-input srce-goal-input" value={text} maxLength={GOAL_MAX} autoFocus disabled={busy}
            aria-label="Goal" placeholder="Type a goal, or pick one" onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              // Not while an input method is composing text (Codex S2a r1 #6).
              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
              if (e.key === 'Enter' && !busy) save();
              if (e.key === 'Escape') setEditing(null);
            }} />
          <span className="srce-row">
            {GOAL_PICKS.map((g) => (
              <button key={g} type="button" className={`srce-q${cleanGoal(text) === g ? ' srce-q--approved' : ''}`}
                disabled={busy} onClick={() => setText(g)}>{g}</button>
            ))}
          </span>
          </>)}
          <span className="srce-row">
            <button type="button" className="srce-btn srce-btn--primary" disabled={busy || tooLong} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
            <button type="button" className="ll-clr" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
          </span>
          {error && <span className="srce-error" role="alert">{error}</span>}
        </div>
      )}
      {notice && editing === null && <div className="srce-mut srce-card2-f" role="status">{notice}</div>}
    </section>
  );
}

function activityText(a) {
  if (a.kind === 'supplier') {
    return a.event === 'declined'
      ? `${a.supplier_name} declined${a.reason ? `: "${a.reason}"` : '.'}`
      : `${a.supplier_name} submitted a bid.`;
  }
  // `what` (Lane 1388, helpers/sourcing_activity.py) names what the change touched
  // ("Added Chrysler Encapsulated Seals, Inc."); older rows fall back to the action words.
  const what = a.what || ACTION_TEXT[a.action] || 'Changed the event';
  if (!a.user_name) return what.endsWith('.') ? what : `${what}.`;   // "…, Inc." keeps one period
  return `${what} (${a.user_name}${a.channel === 'chat' ? ', in chat' : ''}).`;
}

// The event terms the buyer set (Lane 1387, Ryan 2026-10-05): "Terms and documents" left
// the event header, so the terms show here, one line each, and "Open" opens the same
// terms and notes panel as before. Every term the panel shows, with the panel's labels
// (eventTerms.js); a long text is cut to one line and shown whole on hover and in the
// panel.
function TermsCard({ summary, onOpenTerms }) {
  const { amendment } = shownTerms(summary);
  return (
    <section className="srce-card2" data-testid="srce-terms">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Terms</span>
        {onOpenTerms && <button type="button" className="ll-clr srce-push" onClick={onOpenTerms}>Open</button>}
      </div>
      <div className="srce-mini">
        {termRows(summary).map(([key, label, value]) => (
          <div key={key} className="srce-mini-r srce-mini--kv">
            <span className="srce-mut srce-ell" title={label}>{label}</span>
            <span className={`srce-ell${value === 'Not set' ? ' srce-mut' : ''}`} title={value}>{value}</span>
          </div>
        ))}
      </div>
      {amendment && (
        <div className="srce-mut srce-card2-f">These are the terms of the change in progress. Suppliers see them after you publish the change.</div>
      )}
    </section>
  );
}

// Round control (Lane 1362, Ryan): only here, on the Schedule card. The current round's
// chip opens a small menu. "Open": close the round or extend its due date; "Closed" (the
// event waits for the next step): feedback and the next round. Each item opens Rounds and
// feedback at its own confirmation or form and uses the existing actions. Dismissed with
// X, Escape or a click outside. Earlier and planned rounds have no menu.
function RoundMenu({ round, closed, onOpenRound }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const chip = useRef(null);
  const first = useRef(null);
  const dismiss = () => { setOpen(false); if (chip.current) chip.current.focus(); };
  useEffect(() => {
    if (!open) return undefined;
    if (first.current) first.current.focus();
    const key = (e) => { if (e.key === 'Escape') { setOpen(false); if (chip.current) chip.current.focus(); } };
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener('keydown', key);
    document.addEventListener('mousedown', away);
    return () => { document.removeEventListener('keydown', key); document.removeEventListener('mousedown', away); };
  }, [open]);
  const pick = (mode) => { setOpen(false); onOpenRound(mode); };
  return (
    <span className="srce-push srce-dd" ref={wrap}>
      <button type="button" ref={chip} className={`srce-pill srce-pill--btn ${closed ? 'srce-pill--gray' : 'srce-pill--info'}`}
        aria-haspopup="menu" aria-expanded={open} aria-label={`Round ${round} ${closed ? 'closed' : 'open'}: round actions`}
        onClick={() => setOpen(!open)}>{closed ? 'Closed' : 'Open'}</button>
      {open && (
        <span className="srce-ddpanel srce-ddpanel--right srce-roundmenu" role="menu" aria-label={`Round ${round}`}>
          <span className="srce-roundmenu-h">
            <span className="srce-strong">Round {round}</span>
            <button type="button" className="srce-roundmenu-x" aria-label="Close menu" onClick={dismiss}>×</button>
          </span>
          {closed ? (
            <button type="button" role="menuitem" ref={first} className="srce-roundmenu-i" onClick={() => pick(null)}>Feedback and next round</button>
          ) : (
            <>
              <button type="button" role="menuitem" ref={first} className="srce-roundmenu-i" onClick={() => pick('close')}>Close round {round}</button>
              <button type="button" role="menuitem" className="srce-roundmenu-i" onClick={() => pick('extend')}>Extend due date</button>
            </>
          )}
        </span>
      )}
    </span>
  );
}

export default function OverviewTab({ api, eventId, summary, refreshKey, onChanged, onOpenParts, onOpenSuppliers, onOpenScenarios, onOpenHistory,
  onOpenRound, onOpenTerms, onGo, saveQueue }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(null);
  const [notice, setNotice] = useState('');
  const [tick, setTick] = useState(0);
  // Lane 1440: "Set offers due" from the tracker opens that field on the RFP setup card.
  const [editDueAt, setEditDueAt] = useState(null);
  // Lane 1440: the Overview section shown on the right, kept for this browser tab.
  const sectionKey = `srce-ov-section:${eventId}`;
  const [section, setSection] = useState(() => {
    try { return window.sessionStorage.getItem(sectionKey) || 'setup'; } catch (e) { return 'setup'; }
  });
  useEffect(() => {
    try { setSection(window.sessionStorage.getItem(`srce-ov-section:${eventId}`) || 'setup'); } catch (e) { setSection('setup'); }
  }, [eventId]);
  const pick = (key) => {
    setSection(key);
    try { window.sessionStorage.setItem(sectionKey, key); } catch (e) { /* storage blocked */ }
  };
  // A goal the chat card could not save (negotiation PLAN section 4), for this event
  // only; cleared from the history entry once the goal is saved (Codex S2a r1 #4).
  const location = useLocation();
  const navigate = useNavigate();
  const goalNotice = (location.state && location.state.goalNotice
    && location.state.goalEvent === eventId) ? location.state.goalNotice : '';
  // Figures and an open goal edit belong to one event (Codex S2a r1 #1).
  useEffect(() => { setData(null); }, [eventId]);
  useEffect(() => {
    let live = true;
    api.overview(eventId).then((d) => { if (live) { setData(d); setError(''); } })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [api, eventId, refreshKey, tick]);
  // The recommendation calculates on its own (Lane 1393): when it is out of date, and the
  // first time once a bid is in. While it calculates, look again every 3 seconds.
  const recNow = data && data.recommendation;
  const autoItems = useMemo(() => {
    if (!recNow || !data || !['open', 'closed'].includes(data.status)) return [];
    const due = recNow.state === 'out_of_date' || (recNow.state === 'not_calculated' && data.bids.received > 0);
    return due ? [{ scenario_id: recNow.scenario_id, key: `${recNow.scenario_id}:${recNow.result_id || 'none'}` }] : [];
  }, [recNow, data]);
  useAutoRecalculate(api, eventId, autoItems, () => setTick((t) => t + 1));
  const recCalculating = Boolean(recNow && recNow.state === 'calculating');
  useEffect(() => {
    if (!recCalculating) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), 3000);
    return () => clearTimeout(t);
  }, [recCalculating, data]);
  const saved = ({ stale }) => {
    setSettings(null);
    setNotice(stale ? 'The schedule changed while you had it open. This is the schedule now; click Open again to change it.' : '');
    setTick((t) => t + 1);
    if (onChanged) onChanged();
  };
  // A failed refresh keeps the loaded overview (and an open goal edit) on screen
  // (Codex S2a r3 #2).
  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading…</div>;

  const isDraft = data.status === 'draft';
  const round = data.rounds.find((r) => r.round === data.current_round);
  const rec = recommendationTile(data.recommendation, isDraft);
  const recDone = data.recommendation && data.recommendation.state === 'done';
  const recEarlier = isEarlier(data.recommendation);
  const chgNow = recDone || recEarlier ? change(data.recommendation.change) : null;
  const chg = chgNow && recEarlier ? { ...chgNow, tone: null } : chgNow;
  const plan = planRows(data);
  // Lane 1440 (SOURCING_INVITATIONS_ENABLED; the server sends `invitations` only then):
  // the tracker and the RFP setup card, which takes the Schedule card's place.
  const inv = data.invitations || null;
  const roundControl = data.rounds.some((r) => r.round === data.current_round) && onOpenRound
    && ['open', 'closed'].includes(data.status)
    ? <RoundMenu round={data.current_round} closed={Boolean((round || {}).closed_at) || data.status === 'closed'} onOpenRound={onOpenRound} />
    : null;
  const sectioned = Boolean(inv && inv.setup);
  const go = (where) => {
    if (where === 'offers_due') { setEditDueAt(Date.now()); return; }
    if (onGo) onGo(where);
  };
  const goalSaved = () => {
    if (goalNotice) navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    setTick((t) => t + 1);
    if (onChanged) onChanged();
  };
  // The Overview's sections, each built once: the long page shows them in order; with
  // RFP invitations on (Lane 1440, Ryan's staging review) they are a menu and a panel.
  const statusCard = (
    <section className="srce-card2">
      <div className="srce-card2-h"><span className="srce-card2-t">{sectioned ? "Wingman's recommendation" : 'Current status'}</span></div>
      <div className="srce-tiles">
        <StatTile label={isDraft ? 'Draft' : `Round ${data.current_round || 1}`}
          value={isDraft ? 'Not published' : round && !round.closed_at ? `Due ${shortDate(round.due_at)}` : 'Closed'}
          note={isDraft ? 'Suppliers see nothing yet' : round ? formatDueAt(round.closed_at || round.due_at) : ''} />
        <StatTile label="Bids received" value={isDraft ? '-' : `${data.bids.quoted} of ${data.bids.suppliers} quoted`}
          note={bidsNote(data)} />
        <StatTile label="Wingman's recommendation" value={rec.value} tone={rec.warn ? 'crit' : null} note={rec.note}
          onClick={onOpenScenarios} disabled={isDraft} />
        {/* Today is the last 12 months' average price paid (Ryan, redesign slice 2);
            a change that leaves out parts without a price today says so. */}
        <StatTile label="Change against today" value={chg ? chg.value : '-'} tone={chg ? chg.tone : null}
          note={recEarlier && chg ? `Earlier figure${data.recommendation.change_lines
            ? `, on ${data.recommendation.change_lines.known} of ${data.recommendation.change_lines.of} parts` : ''}`
            : !recDone ? '' : data.recommendation.change_lines
            ? `On ${data.recommendation.change_lines.known} of ${data.recommendation.change_lines.of} parts`
            : "Against the last 12 months' prices"} />
      </div>
    </section>
  );
  const partsCard = (
    <section className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Parts</span>
        <button type="button" className="ll-clr srce-push" onClick={onOpenParts}>{sectioned ? 'Open Parts' : 'Open'}</button>
      </div>
      <div className="srce-mini srce-mini--3">
        <div className="srce-mini-h"><span>Category</span><span className="srce-wt-num">Parts</span><span className="srce-wt-num">Spend</span></div>
        {data.categories.rows.map((c) => (
          <div key={c.name} className="srce-mini-r"><span className="srce-ell" title={c.label || c.name}>{c.label || c.name}</span>
            <span className="srce-wt-num">{c.parts.toLocaleString()}</span>
            <span className="srce-wt-num">{formatMoney(c.spend)}</span></div>
        ))}
        <div className="srce-mini-r srce-mini-tot"><span>Total</span>
          <span className="srce-wt-num">{data.categories.total.parts.toLocaleString()}</span>
          <span className="srce-wt-num">{formatMoney(data.categories.total.spend)}</span></div>
      </div>
      <div className="srce-mut srce-card2-f">Spend: the last 12 months, taken when the event was created.</div>
    </section>
  );
  const suppliersCard = (
    <section className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Suppliers</span>
        <button type="button" className="ll-clr srce-push" onClick={onOpenSuppliers}>{sectioned ? 'Open Suppliers' : 'Open'}</button>
      </div>
      <div className="srce-mini">
        {data.suppliers.length === 0 && <div className="srce-mut srce-mini-r">No suppliers yet.</div>}
        {data.suppliers.map((s) => (
          <div key={s.supplier_id} className="srce-mini-r srce-mini--2">
            <span className="srce-ell" title={s.supplier_name}>{s.supplier_name}</span>
            <span className={`srce-push ${RESPONSE_PILL[s.response] || 'srce-pill srce-pill--info'}`}>{s.response_text}</span>
          </div>
        ))}
      </div>
    </section>
  );
  const scheduleCard = (
    <section className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Schedule</span>
        {/* "Open", like the other cards (Lane 1387, Ryan); it opens the schedule settings. */}
        {data.schedule && data.schedule.can_edit && (
          <button type="button" className="ll-clr srce-push" aria-label="Open the schedule settings"
            onClick={() => { setNotice(''); setSettings(data); }}>Open</button>)}
      </div>
      <div className="srce-mini">
        {isDraft && !plan.rows.some((r) => r.round === 1) && <div className="srce-mini-r srce-mut">Round 1 opens when you publish.</div>}
        {data.rounds.map((r) => (
          <div key={r.round} className="srce-mini-r srce-mini--3s">
            <span>Round {r.round}</span>
            <span className="srce-mut srce-ell">{shortDate(r.opened_at)} to {shortDate(r.closed_at || r.due_at)}</span>
            {r.round === data.current_round && onOpenRound && ['open', 'closed'].includes(data.status)
              ? <RoundMenu round={r.round} closed={Boolean(r.closed_at) || data.status === 'closed'} onOpenRound={onOpenRound} />
              : <span className={`srce-push srce-pill ${r.closed_at ? 'srce-pill--gray' : 'srce-pill--info'}`}>{r.closed_at ? 'Closed' : 'Open'}</span>}
          </div>
        ))}
        {plan.rows.map((r) => (
          <div key={`plan-${r.round}`} className="srce-mini-r srce-mini--3s">
            <span>Round {r.round}</span>
            <span className="srce-mut srce-ell">{r.day ? `Due ${formatDate(r.day)}` : 'No date yet'}</span>
            <span className={`srce-push srce-pill ${PLAN_PILL[r.state]}`}>{PLAN_STATE_TEXT[r.state]}</span>
          </div>
        ))}
        {plan.award && (
          <div className="srce-mini-r srce-mini--3s">
            <span>Award by</span>
            <span className="srce-mut srce-ell">{formatDate(plan.award)}</span>
            <span className={`srce-push srce-pill ${plan.awardConflict ? 'srce-pill--warn' : 'srce-pill--info'}`}>{plan.awardConflict ? 'Needs updating' : 'Planned'}</span>
          </div>
        )}
      </div>
      {notice && <div className="srce-mut srce-card2-f" role="status">{notice}</div>}
    </section>
  );
  const activityCard = (
    <section className="srce-card2">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Activity</span>
        {data.activity.length > 0 && <button type="button" className="ll-clr srce-push" onClick={onOpenHistory}>{data.activity_more ? 'Show all' : 'History'}</button>}
      </div>
      <div className="srce-mini">
        {data.activity.length === 0 && <div className="srce-mini-r srce-mut">Nothing yet.</div>}
        {data.activity.map((a, i) => (
          <div key={`${a.at}-${i}`} className="srce-mini-r srce-mini--act">
            <span className="srce-mut">{shortDate(a.at)}</span>
            <span className="srce-ell" title={activityText(a)}>{activityText(a)}</span>
          </div>
        ))}
      </div>
    </section>
  );
  const goalCard = (
    <GoalCard key={eventId} api={api} eventId={eventId} data={data} onSaved={goalSaved}
      onStale={() => setTick((t) => t + 1)} notice={goalNotice} />
  );
  // The overview as it was when the schedule settings opened (values and revision together):
  // a refresh behind the modal never pairs old values with a newer revision (Codex s3 r1 #2).
  const settingsModal = settings && (
    <ScheduleSettings api={api} eventId={eventId} overview={settings} onClose={() => setSettings(null)} onSaved={saved} />
  );

  if (sectioned) {
    const termsSet = summary ? termRows(summary).filter(([, , v]) => v !== 'Not set').length : 0;
    const supplierCount = data.suppliers.filter((s) => s.response !== 'removed').length;
    const setupFact = isDraft
      ? (inv.setup.launch_date ? `Launch ${shortDate(`${inv.setup.launch_date}T00:00:00`)}` : 'Launch date not set')
      : `Round ${data.current_round || 1}${round && round.due_at && !round.closed_at ? `, due ${shortDate(round.due_at)}` : ''}`;
    const sections = [
      ['setup', 'RFP setup', setupFact, (
        <>
          <RfpSetupCard key={eventId} api={api} eventId={eventId} data={data} saveQueue={saveQueue}
            onChanged={onChanged} onOpenSchedule={() => { setNotice(''); setSettings(data); }}
            roundControl={roundControl} editDueAt={editDueAt} />
          {notice && <div className="srce-mut" role="status">{notice}</div>}
        </>
      )],
      ['recommendation', "Wingman's recommendation", chg ? chg.value : rec.value, statusCard],
      ...(data.recommend_plan ? [['approach', 'Approach',
        `${data.recommend_plan.rules.length} rule${data.recommend_plan.rules.length === 1 ? '' : 's'}`,
        <ApproachCard plan={data.recommend_plan} />]] : []),
      ['goal', 'Goal', data.goal ? 'Set' : 'Not set', goalCard],
      ...(summary ? [['terms', 'Terms', `${termsSet} set`, <TermsCard summary={summary} onOpenTerms={onOpenTerms} />]] : []),
      ['parts', 'Parts', `${data.categories.total.parts.toLocaleString()} parts`, partsCard],
      ['suppliers', 'Suppliers', `${supplierCount} supplier${supplierCount === 1 ? '' : 's'}`, suppliersCard],
      ['activity', 'Activity', data.activity.length ? shortDate(data.activity[0].at) : 'Nothing yet', activityCard],
    ];
    const shown = sections.find(([key]) => key === section) || sections[0];
    return (
      <div className="srce-ov" data-testid="srce-ov-sections">
        {error && (
          <div className="srce-error" role="alert">{error} <button type="button" className="ll-clr" onClick={() => setTick((t) => t + 1)}>Try again</button></div>)}
        {inv.tracker && <InvitationTracker tracker={inv.tracker} onGo={(where) => { if (where === 'offers_due') pick('setup'); go(where); }} />}
        <div className="srce-ovx">
          <nav className="srce-card2 srce-ovx-menu" aria-label="Overview sections">
            {sections.map(([key, label, fact]) => (
              <button key={key} type="button" className={`srce-ovx-i${shown[0] === key ? ' srce-ovx-i--on' : ''}`}
                aria-current={shown[0] === key ? 'true' : undefined} onClick={() => pick(key)}>
                <span className="srce-ovx-l">{label}</span>
                <span className="srce-ovx-f srce-ell" title={fact}>{fact}</span>
              </button>
            ))}
          </nav>
          <div className="srce-ovx-panel" data-testid={`srce-ov-panel-${shown[0]}`}>{shown[3]}</div>
        </div>
        {settingsModal}
      </div>
    );
  }

  return (
    <div className="srce-ov">
      {error && (
        <div className="srce-error" role="alert">{error} <button type="button" className="ll-clr" onClick={() => setTick((t) => t + 1)}>Try again</button></div>)}
      {goalCard}
      {data.recommend_plan && <ApproachCard plan={data.recommend_plan} />}
      {statusCard}

      <div className="srce-grid3">
        {partsCard}
        {suppliersCard}
        {scheduleCard}
      </div>

      {summary && <TermsCard summary={summary} onOpenTerms={onOpenTerms} />}

      {settingsModal}

      {activityCard}
    </div>
  );
}
